import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {HiChevronDown} from 'react-icons/hi';
import {
  ENTITY,
  setProjectPeriod,
  setProjectPeriodAuto,
  setRoadmapPeriod,
  setRoadmapPeriodAuto
} from "../../redux/slices/timeline";
import {ViewType} from "../Timeline/types";
import {calculateWorkingDaysBetweenDates, defaultWorkingDays} from "../../utils/helperUtils";

interface BestFitPeriodSelectorProps {
  period: ViewType;
  setPeriod: (period: ViewType) => void;
  simFlag?: boolean;
  autoMode?: boolean;
  onAutoModeChange?: (auto: boolean) => void;
}

const BestFitPeriodSelector: React.FC<BestFitPeriodSelectorProps> = ({ 
  period, 
  setPeriod, 
  simFlag = false,
  autoMode,
  onAutoModeChange
}) => {
  const dispatch = useDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    singleRoadmapRes,
    data: projectTasks = [],
  } = useSelector((state: any) => state.roadmap);

  const {
    currentEntity,
    roadmapPeriodAuto,
    projectPeriodAuto,
  } = useSelector((state: any) => state.timeline);

  const {
    orgWorkingDays = [],
    orgSchedule: organizationHolidays = [],
  } = useSelector((state: any) => state.organization);

  // Best-fit calculation (moved from TimelineNavBar)
  const workingDaysArray: number[] = Array.isArray(orgWorkingDays) && orgWorkingDays.length > 0 ? orgWorkingDays : defaultWorkingDays;

  const collectDurations = (): number[] => {
    try {
      if (currentEntity === ENTITY.ROADMAP) {
        const projects = Array.isArray(singleRoadmapRes?.projects) ? singleRoadmapRes.projects : [];
        return projects
          .map((p: any) => {
            const start = simFlag ? p?.sim_data?.start_date : p?.start_date;
            const end = simFlag ? p?.sim_data?.end_date : p?.end_date;
            if (!start || !end) return null;
            return calculateWorkingDaysBetweenDates(
              String(start),
              String(end),
              workingDaysArray as number[],
              organizationHolidays as { date: string }[]
            );
          })
          .filter((d: number | null): d is number => typeof d === 'number' && d > 0);
      }

      if (currentEntity === ENTITY.PROJECT) {
        const tasks = Array.isArray(projectTasks) ? projectTasks : [];
        return tasks
          .slice(1)
          .map((t: any) => {
            const start = t?.start || t?.adj_start_date;
            const end = t?.end || t?.due_date;
            if (!start || !end) return null;
            return calculateWorkingDaysBetweenDates(
              String(start),
              String(end),
              workingDaysArray as number[],
              organizationHolidays as { date: string }[]
            );
          })
          .filter((d: number | null): d is number => typeof d === 'number' && d > 0);
      }
    } catch (e) {
      return [];
    }
    return [];
  };

  const computeWeightedMeanByMagnitude = (
    values: number[],
    gamma: number = 2,
    alpha: number = 0.75,
    wMin: number = 0.25,
    wMax: number = 4
  ): number | null => {
    if (!Array.isArray(values) || values.length === 0) return null;
    const nums = values.filter(v => typeof v === 'number' && isFinite(v) && v > 0);
    if (nums.length === 0) return null;

    const sorted = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    const safeMedian = median > 0 ? median : 1;

    const freqMap = new Map<number, number>();
    for (const element of nums) {
      const v = element;
      freqMap.set(v, (freqMap.get(v) || 0) + 1);
    }
    const entries = Array.from(freqMap.entries());

    let weightedSum = 0;
    let weightTotal = 0;
    for (const element of entries) {
      const value = element[0];
      const freq = element[1];
      const freqWeight = Math.pow(freq, gamma);
      const magRaw = Math.pow(value / safeMedian, alpha);
      const magWeight = Math.max(wMin, Math.min(wMax, magRaw));
      const weight = freqWeight * magWeight;
      weightedSum += value * weight;
      weightTotal += weight;
    }
    if (weightTotal === 0) return null;
    return Math.round(weightedSum / weightTotal);
  };

  const computeModeInfo = (values: number[]): { modeValue: number | null; maxFreq: number; total: number } => {
    if (!Array.isArray(values) || values.length === 0) return { modeValue: null, maxFreq: 0, total: 0 };
    const freqMap = new Map<number, number>();
    for (const element of values) {
      const v = element;
      if (!isFinite(v)) continue;
      freqMap.set(v, (freqMap.get(v) || 0) + 1);
    }
    let modeValue: number | null = null;
    let maxFreq = 0;
    const entries = Array.from(freqMap.entries());
    for (const element of entries) {
      const value = element[0];
      const freq = element[1];
      if (freq > maxFreq) {
        maxFreq = freq;
        modeValue = value;
      }
    }
    return { modeValue, maxFreq, total: values.length };
  };

  const getBucketFromDuration = (d: number): ViewType => {
    if (d <= 3) return 'weekly';
    if (d <= 24) return 'monthly';
    if (d <= 48) return 'quarterly';
    return 'yearly';
  };

  const bucketIndex = (b: ViewType): number => {
    switch (b) {
      case 'weekly': return 0;
      case 'monthly': return 1;
      case 'quarterly': return 2;
      case 'yearly': return 3;
      default: return 1;
    }
  };

  const applyBucketBias = (b: ViewType, bias: number): ViewType => {
    const order: ViewType[] = ['weekly', 'monthly', 'quarterly', 'yearly'];
    const idx = bucketIndex(b);
    const nextIdx = Math.max(0, Math.min(order.length - 1, idx + Math.max(0, Math.floor(bias))));
    return order[nextIdx];
  };

  const computeSparsityBias = (values: number[], baseBucket: ViewType): number => {
    const N = values.length;
    if (N === 0) return 0;
    const uniqueCount = new Set(values).size;
    const uniqueRatio = uniqueCount / N;

    if (N <= 3 && uniqueRatio >= (2 / 3)) {
      if (baseBucket === 'weekly') return 1; 
      return 0;
    }

    const nums = values.filter(v => isFinite(v));
    if (nums.length === 0) return 0;
    let sum = 0;
    for (const element of nums) sum += element;
    const mean = sum / nums.length;
    if (mean <= 0) return 0;
    let sq = 0;
    for (const element of nums) {
      const diff = element - mean;
      sq += diff * diff;
    }
    const variance = sq / nums.length;
    const std = Math.sqrt(variance);
    const cv = std / (mean + 1e-6);

    if (cv >= 1.0) return 2;
    if (cv >= 0.6) return 1;
    return 0;
  };

  const resolveBestFitPeriod = (): ViewType | null => {
    const durations = collectDurations();
    const weightedMeanMag = computeWeightedMeanByMagnitude(durations, 2, 0.75, 0.25, 4);
    const modeInfo = computeModeInfo(durations);

    const representativeBase = weightedMeanMag;
    if (representativeBase == null) return null;

    let baseBucket = getBucketFromDuration(representativeBase);

    const bias = computeSparsityBias(durations, baseBucket);
    let biasedBucket = applyBucketBias(baseBucket, bias);

    const N = durations.length;
    const ratio = N > 0 ? (modeInfo.maxFreq / N) : 0;
    if (N <= 3 && ratio >= (2 / 3) && modeInfo.modeValue != null) {
      const modeBucket = getBucketFromDuration(modeInfo.modeValue);
      if (bucketIndex(modeBucket) >= bucketIndex(biasedBucket)) {
        biasedBucket = modeBucket;
      }
    }

    if (currentEntity === ENTITY.PROJECT && biasedBucket === 'yearly') {
      return 'quarterly';
    }

    return biasedBucket;
  };

  const handleExplicitPeriodChange = (next: ViewType) => {
    if (onAutoModeChange) {
      onAutoModeChange(false);
    } else {
      if (currentEntity === ENTITY.ROADMAP) {
        dispatch(setRoadmapPeriodAuto(false));
      } else if (currentEntity === ENTITY.PROJECT) {
        dispatch(setProjectPeriodAuto(false));
      }
    }
    
    if (currentEntity === ENTITY.ROADMAP) {
      dispatch(setRoadmapPeriod(next));
    } else if (currentEntity === ENTITY.PROJECT) {
      dispatch(setProjectPeriod(next));
    }
    setPeriod(next);
    setDropdownOpen(false);
  };

  const handleBestFitSelect = () => {
    const resolved = resolveBestFitPeriod();
    if (!resolved) {
      const fallback: ViewType = currentEntity === ENTITY.ROADMAP ? 'monthly' : 'weekly';
      if (onAutoModeChange) {
        onAutoModeChange(true);
      } else {
        if (currentEntity === ENTITY.ROADMAP) {
          dispatch(setRoadmapPeriodAuto(true));
        } else if (currentEntity === ENTITY.PROJECT) {
          dispatch(setProjectPeriodAuto(true));
        }
      }

      if (currentEntity === ENTITY.ROADMAP) {
        dispatch(setRoadmapPeriod(fallback));
      } else if (currentEntity === ENTITY.PROJECT) {
        dispatch(setProjectPeriod(fallback));
      }
      setPeriod(fallback);
      setDropdownOpen(false);
      return;
    }

    if (onAutoModeChange) {
      onAutoModeChange(true);
    } else {
      if (currentEntity === ENTITY.ROADMAP) {
        dispatch(setRoadmapPeriodAuto(true));
      } else if (currentEntity === ENTITY.PROJECT) {
        dispatch(setProjectPeriodAuto(true));
      }
    }

    if (currentEntity === ENTITY.ROADMAP) {
      dispatch(setRoadmapPeriod(resolved));
    } else if (currentEntity === ENTITY.PROJECT) {
      dispatch(setProjectPeriod(resolved));
    }
    setPeriod(resolved);
    setDropdownOpen(false);
  };

  const periodAuto = useMemo(() => {
    if (autoMode !== undefined) return autoMode;
    return currentEntity === ENTITY.ROADMAP ? roadmapPeriodAuto : projectPeriodAuto
  }, [currentEntity, roadmapPeriodAuto, projectPeriodAuto, autoMode])

  useEffect(() => {
    const shouldAuto = !!periodAuto;
    if (!shouldAuto) return;
    const resolved = resolveBestFitPeriod();
    if (!resolved) return;
    if (resolved !== period) {
      if (currentEntity === ENTITY.ROADMAP) {
        dispatch(setRoadmapPeriod(resolved));
      } else if (currentEntity === ENTITY.PROJECT) {
        dispatch(setProjectPeriod(resolved));
      }
      setPeriod(resolved);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simFlag, currentEntity, singleRoadmapRes, projectTasks, organizationHolidays, orgWorkingDays]);

  return (
    <div className="relative ml-2">
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center px-3.5 py-1.5 text-sm rounded-lg bg-light-surfacePrimary dark:bg-slate-700 text-light-text-primary dark:text-gray-300 font-medium border border-light-border-default dark:border-gray-600 hover:bg-light-interactive-hover dark:hover:bg-slate-600 transition-colors duration-200"
      >
        <span className="mr-2 capitalize">
          {periodAuto
            ? `Best Fit`
            // ? `Best fit (${period})`
            : `${period} View`}
        </span>
        <HiChevronDown className={`text-light-text-secondary transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
      </button>

      {dropdownOpen && (
        <>
          <div
            className="absolute top-full left-0 mt-1 w-40 bg-light-surfacePrimary  dark:bg-secondary-dark rounded-lg shadow-lg border border-light-border-default dark:border-gray-700 overflow-hidden"
            style={{ zIndex: 9999 }}
          >
            <button
              onClick={handleBestFitSelect}
              className={`block w-full text-left px-4 py-2.5 text-sm text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200 
                ${periodAuto ? 'bg-light-interactive-selected dark:bg-slate-700 font-medium' : ''}
              `}
            >
              Best Fit (Auto)
            </button>

            {currentEntity === ENTITY.ROADMAP && (
              <button
                onClick={() => handleExplicitPeriodChange("yearly")}
                className={`block w-full text-left px-4 py-2.5 text-sm text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200 ${
                  (!periodAuto && period === "yearly") ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""
                }`}
              >
                Yearly View
              </button>
            )}
            <button
              onClick={() => handleExplicitPeriodChange("quarterly")}
              className={`block w-full text-left px-4 py-2.5 text-sm text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200 ${
                (!periodAuto && period === "quarterly") ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""
              }`}
            >
              Quarterly View
            </button>
            <button
              onClick={() => handleExplicitPeriodChange("monthly")}
              className={`block w-full text-left px-4 py-2.5 text-sm text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200 ${
                (!periodAuto && period === "monthly") ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""
              }`}
            >
              Monthly View
            </button>
            <button
              onClick={() => handleExplicitPeriodChange("weekly")}
              className={`block w-full text-left px-4 py-2.5 text-sm text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200 ${
                (!periodAuto && period === "weekly") ? "bg-light-interactive-selected dark:bg-slate-700 font-medium" : ""
              }`}
            >
              Weekly View
            </button>
          </div>
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setDropdownOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default BestFitPeriodSelector; 